import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Image,
} from "@/ui";
import { Colors } from "@/constants";
import Icon from "./Icon";
import { useUISlice } from "@/models/useUISlice";
import DesktopContainer from "./DesktopContainer";
import MobileContainer from "./MobileContainer";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useAuth } from "@/models/auth";
import Menu from "./Menu";
import { useTranslation } from "react-i18next";
import { getLangDir, getLocalizedBasePath } from "@/models/utils";
import Paywall from "./Paywall";
import { useGoogleAuth } from "@/models/useGoogleAuth";
import { useUserSlice } from "@/models/useUserSlice";

const Header = () => {
  const { t } = useTranslation();
  const isMobile = useUISlice((state) => state.isMobile);
  const router = useRouter();
  const { isFreeUser, currentAuthUser, signIn, signOut, goToDashboard } =
    useAuth();
  const isRTL = getLangDir() === "rtl";
  const authUser = currentAuthUser();
  const [isFree, setIsFree] = useState(useUserSlice.getState().isFree);
  const { signInWithGoogle } = useGoogleAuth();

  useEffect(() => {
    const updateUserStatus = async () => {
      setIsFree(await isFreeUser());
    };

    updateUserStatus();
  }, []);

  const _onSignIn = async () => {
    const signInCallback = async () => {
      const result = await goToDashboard();
      if (!result.success) {
        Paywall.showPaywall();
      }
    };

    signIn({
      onGoogleSignIn: () => signInWithGoogle(signInCallback),
      callback: signInCallback,
    });
  };

  return (
    <View style={{ height: Header.HEIGHT, zIndex: 1000 }}>
      <View
        style={{
          ...s.container,
          paddingHorizontal: isMobile ? 16 : 32,
        }}
      >
        <Pressable
          onPress={
            isFree
              ? () => router.push(getLocalizedBasePath())
              : () => router.push(`${getLocalizedBasePath()}/dashboard`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <View style={s.logoContainer}>
            <Icon icon="logoApp" style={s.logoIcon} />
            <Text
              style={{
                ...s.logoText,
                fontSize: isMobile ? 18 : 24,
                ...(isRTL && { marginLeft: 0, marginRight: 8 }),
              }}
            >
              1Transcribe
            </Text>
          </View>
        </Pressable>

        <MobileContainer>
          <View>
            {isFree && (
              <TouchableOpacity
                onPress={_onSignIn}
                activeOpacity={0.8}
                style={{
                  backgroundColor: Colors.white,
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: 24,
                  borderRadius: 8,
                  height: 38,
                  borderWidth: 2,
                  borderColor: Colors.white,
                }}
              >
                <Text
                  style={{
                    fontWeight: "700",
                    color: Colors.text,
                    fontSize: 16,
                  }}
                >
                  {t("header.signIn")}
                </Text>
              </TouchableOpacity>
            )}

            {!isFree && (
              <Menu
                position={isRTL ? "left" : "right"}
                actions={[
                  {
                    text: t("header.dashboard"),
                    onPress: () =>
                      router.push(`${getLocalizedBasePath()}/dashboard`),
                  },
                  {
                    text: t("header.buyTime"),
                    onPress: () => Paywall.showPaywall(),
                  },
                  {
                    text: t("header.logout"),
                    destructive: true,
                    onPress: signOut,
                  },
                ]}
                actionComponent={
                  <Image
                    source={{ uri: "/profile.png" }}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                    }}
                  />
                }
              />
            )}
          </View>
        </MobileContainer>

        <DesktopContainer>
          {isFree && (
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              {/* <Pressable
                onPress={() => Paywall.showPaywall()}
                style={{
                  marginRight: 24,
                  ...(isRTL && { marginRight: 0, marginLeft: 24 }),
                }}
              >
                <Text style={s.menu}>{t("header.buyTime")}</Text>
              </Pressable> */}

              <TouchableOpacity
                onPress={_onSignIn}
                activeOpacity={0.8}
                style={{
                  backgroundColor: Colors.white,
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: 24,
                  borderRadius: 12,
                  height: 42,
                }}
              >
                <Text
                  style={{
                    fontWeight: "600",
                    color: Colors.heading,
                    fontSize: 18,
                  }}
                >
                  {t("header.signIn")}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {!isFree && (
            <Menu
              actions={[
                {
                  text: t("header.dashboard"),
                  onPress: () =>
                    router.push(`${getLocalizedBasePath()}/dashboard`),
                },
                {
                  text: t("header.buyTime"),
                  onPress: () => Paywall.showPaywall(),
                },
                {
                  text: t("header.logout"),
                  destructive: true,
                  onPress: signOut,
                },
              ]}
              actionComponent={
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <Image
                    source={{ uri: "/profile.png" }}
                    style={{
                      width: 28,
                      height: 28,
                      marginRight: 8,
                      borderRadius: 8,
                      ...(isRTL && { marginRight: 0, marginLeft: 8 }),
                    }}
                  />

                  <Text style={s.menu}>{authUser && authUser.email}</Text>
                </View>
              }
            />
          )}
        </DesktopContainer>
      </View>
    </View>
  );
};

Header.HEIGHT = 60;

const s = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: Header.HEIGHT,
    position: "fixed" as any,
    backgroundColor: Colors.heading,
    top: 0,
    left: 0,
    right: 0,
  },
  logoContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  logoIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
  },
  logoText: {
    color: Colors.white,
    marginLeft: 8,
    fontSize: 24,
    fontWeight: "600",
  },
  menu: {
    fontSize: 18,
    fontWeight: "600",
    color: Colors.white,
  },
});

export default Header;
